import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Shield, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { initMetaPixel, trackPageView as trackMetaPixelPageView } from '@/lib/services/meta-pixel.service';
import { trackPageView as trackGAPageView, isGAReady } from '@/lib/services/google-analytics.service';
import { loadGA } from '@/lib/ga/loadGA';
import { loadPostHog } from '@/lib/posthog/loadPostHog';
import { loadClarity } from '@/lib/clarity/loadClarity';
import { loadGtm } from '@/lib/analytics/load-gtm';
import { trackPageView as trackPostHogPageView, isPostHogReady } from '@/lib/services/posthog.service';

const COOKIE_CONSENT_KEY = 'rakb_cookie_consent';
const COOKIE_PREFERENCES_KEY = 'rakb_cookie_preferences';

export interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
}

const CookieConsent = () => {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    const savedPreferences = localStorage.getItem(COOKIE_PREFERENCES_KEY);

    if (consent === 'accepted' && savedPreferences) {
      try {
        const prefs = JSON.parse(savedPreferences);
        setPreferences(prefs);
        loadAnalytics(prefs);
      } catch (e) {
        if (import.meta.env.DEV) {
          console.error('Error parsing cookie preferences:', e);
        }
      }
    } else {
      setShowBanner(true);
    }
  }, []);

  const loadAnalytics = (prefs: CookiePreferences) => {
    if (prefs.analytics) {
      loadGtm();
      if (import.meta.env.DEV) {
        console.log('[CookieConsent] 🍪 Consentement analytics accepté, chargement Google Analytics...');
      }
      loadGA()
        .then(() => {
          if (import.meta.env.DEV) console.log('[CookieConsent] ✅ Google Analytics chargé');
          const tryTrackPageView = (attempts = 0) => {
            const maxAttempts = 10;
            if (isGAReady()) {
              trackGAPageView();
            } else if (attempts < maxAttempts) {
              setTimeout(() => tryTrackPageView(attempts + 1), 100);
            }
          };
          setTimeout(() => tryTrackPageView(), 200);
        })
        .catch((error) => {
          console.error('[CookieConsent] ❌ Erreur chargement Google Analytics:', error);
        });

      loadClarity().catch((error) => {
        if (import.meta.env.DEV) {
          console.error('[CookieConsent] ❌ Erreur chargement Microsoft Clarity:', error);
        }
      });
    }

    if (prefs.analytics && import.meta.env.VITE_PLAUSIBLE_DOMAIN) {
      if (typeof window !== 'undefined' && !(window as Window & { plausible?: unknown }).plausible) {
        const script = document.createElement('script');
        script.defer = true;
        script.dataset.domain = import.meta.env.VITE_PLAUSIBLE_DOMAIN;
        script.src = 'https://plausible.io/js/script.js';
        document.head.appendChild(script);
      }
    }

    if (prefs.analytics && import.meta.env.VITE_PUBLIC_POSTHOG_KEY) {
      if (import.meta.env.DEV) console.log('[CookieConsent] 🍪 Chargement PostHog...');
      loadPostHog()
        .then(() => {
          if (import.meta.env.DEV) console.log('[CookieConsent] ✅ PostHog chargé');
          const tryTrackPageView = (attempts = 0) => {
            const maxAttempts = 10;
            if (isPostHogReady()) {
              trackPostHogPageView();
            } else if (attempts < maxAttempts) {
              setTimeout(() => tryTrackPageView(attempts + 1), 100);
            }
          };
          setTimeout(() => tryTrackPageView(), 200);
        })
        .catch((error) => {
          console.error('[CookieConsent] ❌ Erreur chargement PostHog:', error);
        });
    }

    if (prefs.marketing && import.meta.env.VITE_META_PIXEL_ID) {
      initMetaPixel()
        .then(() => trackMetaPixelPageView())
        .catch((error) => {
          if (import.meta.env.DEV) console.error('Error initializing Meta Pixel:', error);
        });
    }
  };

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      essential: true,
      analytics: true,
      marketing: true,
    };
    saveConsent(allAccepted);
  };

  const handleRejectAll = () => {
    const onlyEssential: CookiePreferences = {
      essential: true,
      analytics: false,
      marketing: false,
    };
    saveConsent(onlyEssential);
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
    setShowSettings(false);
  };

  const saveConsent = (prefs: CookiePreferences) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted');
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
    setShowBanner(false);
    loadAnalytics(prefs);
  };

  if (!showBanner && !showSettings) {
    return null;
  }

  return (
    <div
      role="dialog"
      aria-label={t('legal.cookieConsent.title')}
      data-cookie-consent
      className={cn(
        'fixed inset-x-0 z-50 transition-all duration-300 ease-out',
        showBanner || showSettings ? 'bottom-0' : '-bottom-full'
      )}
    >
      {/* Backdrop when settings open (mobile) */}
      {showSettings && (
        <button
          type="button"
          aria-label={t('legal.cookieConsent.cancel')}
          className="absolute inset-0 bg-black/20 sm:bg-transparent sm:pointer-events-none"
          onClick={() => setShowSettings(false)}
        />
      )}

      <div className="px-4 pb-4 sm:px-4 sm:mb-4 max-h-[90vh] overflow-y-auto">
        <div
          className={cn(
            'relative bg-card border-t border-border shadow-premium',
            'sm:rounded-xl sm:max-w-2xl sm:mx-auto'
          )}
        >
        {showSettings ? (
          /* Settings panel */
          <div className="p-4 sm:p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" aria-hidden />
                {t('legal.cookieConsent.title')}
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSettings(false)}
                className="shrink-0 -mr-2"
                aria-label={t('legal.cookieConsent.cancel')}
              >
                <ChevronDown className="h-5 w-5 sm:hidden" aria-hidden />
                <span className="hidden sm:inline">{t('legal.cookieConsent.cancel')}</span>
              </Button>
            </div>

            <p className="text-sm text-muted-foreground">
              {t('legal.cookieConsent.description')}
            </p>

            <div className="space-y-3">
              {/* Essential - always on */}
              <div className="flex items-start justify-between gap-4 p-3 rounded-lg bg-muted/50 border border-border">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm text-foreground">
                    {t('legal.cookieConsent.essentialTitle')}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {t('legal.cookieConsent.essentialDesc')}
                  </p>
                </div>
                <span className="text-xs text-muted-foreground whitespace-nowrap pt-1">
                  {t('legal.cookieConsent.essentialAlwaysOn')}
                </span>
              </div>

              {/* Analytics */}
              <div className="flex items-start justify-between gap-4 p-3 rounded-lg bg-muted/50 border border-border">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm text-foreground">
                    {t('legal.cookieConsent.analyticsTitle')}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {t('legal.cookieConsent.analyticsDesc')}
                  </p>
                </div>
                <Switch
                  id="cookie-analytics"
                  checked={preferences.analytics}
                  onCheckedChange={(checked) =>
                    setPreferences((p) => ({ ...p, analytics: checked }))
                  }
                  aria-label={t('legal.cookieConsent.analyticsTitle')}
                  className="shrink-0 mt-0.5"
                />
              </div>

              {/* Marketing */}
              <div className="flex items-start justify-between gap-4 p-3 rounded-lg bg-muted/50 border border-border">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm text-foreground">
                    {t('legal.cookieConsent.marketingTitle')}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {t('legal.cookieConsent.marketingDesc')}
                  </p>
                </div>
                <Switch
                  id="cookie-marketing"
                  checked={preferences.marketing}
                  onCheckedChange={(checked) =>
                    setPreferences((p) => ({ ...p, marketing: checked }))
                  }
                  aria-label={t('legal.cookieConsent.marketingTitle')}
                  className="shrink-0 mt-0.5"
                />
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowSettings(false)}
                className="w-full sm:w-auto min-h-[44px] sm:min-h-0"
              >
                {t('legal.cookieConsent.cancel')}
              </Button>
              <Button onClick={handleSavePreferences} className="w-full sm:flex-1 min-h-[44px] sm:min-h-0">
                {t('legal.cookieConsent.savePreferences')}
              </Button>
            </div>
          </div>
        ) : (
          /* Main banner */
          <div className="p-4 sm:p-5">
            <div className="flex gap-3 sm:gap-4">
              <Shield
                className="h-10 w-10 sm:h-11 sm:w-11 shrink-0 text-primary rounded-full bg-primary/10 p-2"
                aria-hidden
              />
              <div className="min-w-0 flex-1">
                <h2 className="text-base sm:text-lg font-semibold text-foreground">
                  {t('legal.cookieConsent.title')}
                </h2>
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2 sm:line-clamp-none">
                  {t('legal.cookieConsent.intro')}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  {t('legal.cookieConsent.moreInfo')}{' '}
                  <a
                    href="/legal/privacy"
                    className="text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
                  >
                    {t('legal.privacyLink')}
                  </a>
                  .
                </p>
              </div>
            </div>

            <div className="mt-4 sm:mt-5 flex flex-col sm:flex-row gap-2 sm:gap-3">
              <Button
                onClick={handleAcceptAll}
                className="w-full sm:flex-1 min-h-[44px] sm:min-h-10 order-1"
                aria-label={t('legal.cookieConsent.acceptAll')}
              >
                {t('legal.cookieConsent.acceptAll')}
              </Button>
              <div className="flex flex-col min-[480px]:flex-row gap-2 order-2">
                <Button
                  variant="outline"
                  onClick={handleRejectAll}
                  className="w-full min-[480px]:flex-1 min-h-[44px] sm:min-h-10"
                  aria-label={t('legal.cookieConsent.rejectAll')}
                >
                  {t('legal.cookieConsent.rejectAll')}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setShowSettings(true)}
                  className="w-full min-[480px]:flex-1 min-h-[44px] sm:min-h-10 text-muted-foreground hover:text-foreground"
                  aria-label={t('legal.cookieConsent.customize')}
                >
                  {t('legal.cookieConsent.customize')}
                </Button>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
